import { AxiosError, type AxiosResponse } from 'axios';

import { appConfig } from '@ll-platform/frontend/config/app.config';
import { organizationHttpHeader } from '@ll-platform/frontend/consts/organization';
import type { UserTokens } from '@ll-platform/frontend/core/auth/types';
import {
  clearUserTokens,
  getUserTokens,
} from '@ll-platform/frontend/core/auth/utils/localStorage';
import { organizationService } from '@ll-platform/frontend/core/organization/async/OrganizationsService';
import { Pages } from '@ll-platform/frontend/core/router/pages';
import { makeNextParamForCurrent } from '@ll-platform/frontend/utils/helpers/navigation';

import { HttpClient, type RequestConfig } from './HttpClient';

export type ApiResponse<T> = {
  status: string;
  data: T;
  code: number;
};

export type HttpRequestArgs = {
  config: RequestConfig;
  handleUnauthorized?: (() => void) | false;
};

class HeroHttpClient extends HttpClient {
  public async unwrappedHttpRequest<T>(args: HttpRequestArgs): Promise<T> {
    const response = await this.httpRequest<ApiResponse<T>>(args);

    return response.data?.data;
  }

  public async httpRequest<T>({
    config,
    handleUnauthorized = this.defaultHandleUnauthorized.bind(this),
  }: HttpRequestArgs): Promise<AxiosResponse<T>> {
    const authHeaders = await this.getAuthHeaders();

    const mergedConfig = {
      baseURL: appConfig.REACT_APP_API_URL,
      ...config,
      headers: {
        ...authHeaders,
        ...config.headers,
      },
    } satisfies RequestConfig;
    try {
      const axiosResponse = await super.httpRequest<T>({
        config: mergedConfig,
      });

      return axiosResponse;
    } catch (error) {
      if (
        error instanceof AxiosError &&
        [401].includes(error.response?.status ?? 0) &&
        handleUnauthorized
      ) {
        handleUnauthorized();
      }

      throw error;
    }
  }

  public async defaultHandleUnauthorized(): Promise<void> {
    clearUserTokens();
    window.location.assign(`${Pages.Login}?${makeNextParamForCurrent()}`);
    throw new Error('Unauthorized. Please log in again');
  }

  protected getUserTokens(): UserTokens | null {
    return getUserTokens();
  }

  public async getAuthHeaders(): Promise<Record<string, string>> {
    const tokens = this.getUserTokens();
    const headers = new Headers();

    const organizationId = organizationService.getOrganizationId();
    if (organizationId) {
      headers.set(organizationHttpHeader, organizationId);
    }

    if (tokens?.accessToken) {
      headers.set('Authorization', `Bearer ${tokens.accessToken}`);
    }

    return Object.fromEntries(headers.entries());
  }
}

export const heroHttpClient = new HeroHttpClient();
